// Admin Users (Super Admin Panel) — '/admin/users'.
//
// Ports the legacy SuperAdminPanel from
// www/livevane.com/htdocs/admin/index.html (~lines 997–1256) into a
// TanStack file-based route under the /admin layout.
//
// Auth gate:
//   The parent /admin layout gates on a valid admin session; this route
//   additionally requires the super_admin tier (mirrors the server-side
//   require_super_admin() on every /api/admin/users* and /api/admin/invites*).
//
// Security:
//   - All HTTP calls go through `apiClient`. The bearer token never leaves
//     api-client.ts and is never echoed into the DOM or logs.
//   - Destructive actions (revoke admin, invalidate invite, role change)
//     confirm through <ConfirmDialog>, which returns focus to the button
//     that opened it.
//   - Server error messages flow through ApiError; we render the message
//     string only, into a single `role="alert"` region.
//
// Section order answers "who has access?" first: Admin accounts, then
// Pending invites, Invite history, and finally the invite form.

import { useRef, useState, type FormEvent, type ReactNode } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuthStore } from '@/stores/auth-store'

import { InlineError } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SectionCard } from '@/components/ui/section-card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ResponsiveRows,
  StackedRows,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiClient, ApiError } from '@/lib/api-client'
import { formatDateTime } from '@/lib/format'
import { cn } from '@/lib/utils'
import type {
  AdminInvite,
  AdminInviteCreateResponse,
  AdminInvitesResponse,
  AdminMutationResponse,
  AdminRole,
  AdminUser,
  AdminUsersResponse,
} from '@/lib/types'

export const Route = createFileRoute('/admin/users')({
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role
    if (role !== 'super_admin') {
      throw redirect({ to: '/admin', replace: true })
    }
  },
  component: AdminUsersPage,
})

// The /admin/users and /admin/invites endpoints are super_admin-only and
// return the GLOBAL lists — they intentionally ignore ?view_as.
const QK_USERS = ['admin', 'users'] as const
const QK_INVITES = ['admin', 'invites'] as const

type PendingAction =
  | { kind: 'revoke-admin'; user: AdminUser }
  | { kind: 'revoke-invite'; invite: AdminInvite }
  | { kind: 'change-role'; user: AdminUser; nextRole: AdminRole }
  | null

function describeError(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Unknown error.'
}

function displayName(value: string | null | undefined, emptyLabel: string): string {
  if (value === undefined || value === null || value.trim() === '') return emptyLabel
  return value
}

type InviteStatusKey =
  | 'pending'
  | 'used'
  | 'expired'
  | 'revoked'
  | 'invalidated'
  | 'unknown'

function hasAuditMarker(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return value.trim() !== ''
  return value !== null && value !== undefined
}

function inviteStatusKey(invite: AdminInvite): InviteStatusKey {
  const inviteRecord = invite as AdminInvite & Record<string, unknown>
  const normalized = String(inviteRecord.status ?? '').trim().toLowerCase()

  if (normalized === 'revoked') return 'revoked'
  if (normalized === 'invalidated') return 'invalidated'
  if (hasAuditMarker(inviteRecord.revoked)) return 'revoked'
  if (hasAuditMarker(inviteRecord.revoked_at)) return 'revoked'
  if (hasAuditMarker(inviteRecord.invalidated)) return 'invalidated'
  if (hasAuditMarker(inviteRecord.invalidated_at)) return 'invalidated'
  if (normalized === 'pending') return 'pending'
  if (normalized === 'used') return 'used'
  if (normalized === 'expired') return 'expired'
  return 'unknown'
}

const INVITE_STATUS_LABEL: Record<InviteStatusKey, string> = {
  pending: 'Pending',
  used: 'Used',
  expired: 'Expired',
  revoked: 'Revoked',
  invalidated: 'Invalidated',
  unknown: 'Unknown',
}

function Pill({ tone, children, title }: { tone: 'cyan' | 'muted'; children: ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium',
        tone === 'cyan'
          ? 'border-cyber-cyan/40 bg-cyber-cyan/10 text-cyber-cyan'
          : 'border-border/60 bg-muted/40 text-muted-foreground',
      )}
    >
      {children}
    </span>
  )
}

function AdminUsersPage() {
  const queryClient = useQueryClient()

  const [email, setEmail] = useState<string>('')
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState<boolean>(false)
  const [pending, setPending] = useState<PendingAction>(null)
  const [inlineError, setInlineError] = useState<string | null>(null)
  // The button that opened the confirmation; focus returns there on close.
  const confirmTriggerRef = useRef<HTMLElement | null>(null)

  const usersQuery = useQuery({
    queryKey: QK_USERS,
    queryFn: () => apiClient.get<AdminUsersResponse>('/admin/users'),
    select: (data: AdminUsersResponse): AdminUser[] => data.admins,
  })

  const invitesQuery = useQuery({
    queryKey: QK_INVITES,
    queryFn: () => apiClient.get<AdminInvitesResponse>('/admin/invites'),
    select: (data: AdminInvitesResponse): AdminInvite[] => data.invites,
  })

  const createInvite = useMutation({
    mutationFn: (input: { email: string }) =>
      apiClient.post<AdminInviteCreateResponse>('/admin/invite', { email: input.email }),
    onSuccess: (data) => {
      setInviteUrl(data.invite_url)
      setEmail('')
      setInlineError(null)
      void queryClient.invalidateQueries({ queryKey: QK_INVITES })
    },
    onError: (err) => {
      setInviteUrl(null)
      setInlineError(describeError(err))
    },
  })

  const changeRole = useMutation({
    mutationFn: (input: { id: number; role: AdminRole }) =>
      apiClient.patch<AdminMutationResponse>(`/admin/users/${input.id}/role`, { role: input.role }),
    onSuccess: () => {
      setInlineError(null)
      setPending(null)
      void queryClient.invalidateQueries({ queryKey: QK_USERS })
    },
    onError: (err) => setInlineError(describeError(err)),
  })

  const deleteUser = useMutation({
    mutationFn: (id: number) => apiClient.del<AdminMutationResponse>(`/admin/users/${id}`),
    onSuccess: () => {
      setInlineError(null)
      setPending(null)
      void queryClient.invalidateQueries({ queryKey: QK_USERS })
    },
    onError: (err) => setInlineError(describeError(err)),
  })

  const revokeInvite = useMutation({
    mutationFn: (id: number) => apiClient.del<AdminMutationResponse>(`/admin/invites/${id}`),
    onSuccess: () => {
      setInlineError(null)
      setPending(null)
      void queryClient.invalidateQueries({ queryKey: QK_INVITES })
    },
    onError: (err) => setInlineError(describeError(err)),
  })

  const handleInviteSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    const trimmed = email.trim()
    if (trimmed === '' || createInvite.isPending) return
    createInvite.mutate({ email: trimmed })
  }

  const handleCopy = async (): Promise<void> => {
    if (inviteUrl === null) return
    try {
      await navigator.clipboard.writeText(inviteUrl)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard blocked by permissions — the URL stays visible for manual copy.
    }
  }

  const openConfirm = (trigger: HTMLElement, action: PendingAction): void => {
    confirmTriggerRef.current = trigger
    setInlineError(null)
    setPending(action)
  }

  const confirmPending = (): void => {
    if (pending === null) return
    if (pending.kind === 'revoke-admin') deleteUser.mutate(pending.user.id)
    else if (pending.kind === 'revoke-invite') revokeInvite.mutate(pending.invite.id)
    else changeRole.mutate({ id: pending.user.id, role: pending.nextRole })
  }
  const confirmBusy = deleteUser.isPending || revokeInvite.isPending || changeRole.isPending

  const admins: AdminUser[] = usersQuery.data ?? []
  const invites: AdminInvite[] = invitesQuery.data ?? []
  const pendingInvites = invites.filter((inv) => inviteStatusKey(inv) === 'pending')
  const inviteHistory = invites.filter((inv) => inviteStatusKey(inv) !== 'pending')

  // ── Row renderers shared by the table and the stacked branch ───────
  const adminActions = (admin: AdminUser) => {
    const isSuper = admin.role === 'super_admin'
    const nextRole: AdminRole = isSuper ? 'admin' : 'super_admin'
    const roleChanging = changeRole.isPending && changeRole.variables?.id === admin.id
    const revoking = deleteUser.isPending && deleteUser.variables === admin.id
    const anyPending = changeRole.isPending || deleteUser.isPending
    return (
      <>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="admin-users-role-toggle"
          disabled={anyPending}
          onClick={(e) => openConfirm(e.currentTarget, { kind: 'change-role', user: admin, nextRole })}
        >
          {roleChanging ? 'Updating…' : isSuper ? 'Demote to admin' : 'Promote to super admin'}
        </Button>
        {isSuper ? (
          <Pill tone="muted" title="Super admins can’t be revoked. Demote to admin first.">
            Protected
          </Pill>
        ) : (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            data-testid="admin-users-revoke"
            disabled={anyPending}
            onClick={(e) => openConfirm(e.currentTarget, { kind: 'revoke-admin', user: admin })}
          >
            {revoking ? 'Revoking…' : 'Revoke access'}
          </Button>
        )}
      </>
    )
  }
  const adminBusy = (admin: AdminUser): boolean =>
    (changeRole.isPending && changeRole.variables?.id === admin.id) ||
    (deleteUser.isPending && deleteUser.variables === admin.id)
  const rolePill = (admin: AdminUser) =>
    admin.role === 'super_admin' ? <Pill tone="cyan">Super admin</Pill> : <Pill tone="muted">Admin</Pill>

  const inviteActions = (invite: AdminInvite) => {
    const revokingThis = revokeInvite.isPending && revokeInvite.variables === invite.id
    return (
      <Button
        type="button"
        variant="destructive"
        size="sm"
        data-testid="admin-users-invite-revoke"
        disabled={revokeInvite.isPending}
        onClick={(e) => openConfirm(e.currentTarget, { kind: 'revoke-invite', invite })}
      >
        {revokingThis ? 'Invalidating…' : 'Invalidate'}
      </Button>
    )
  }
  const inviteStatusPill = (invite: AdminInvite) => {
    const key = inviteStatusKey(invite)
    return <Pill tone={key === 'used' ? 'cyan' : 'muted'}>{INVITE_STATUS_LABEL[key]}</Pill>
  }
  const redeemedLabel = (invite: AdminInvite): string =>
    invite.used_at !== null
      ? `${formatDateTime(invite.used_at, { empty: 'Time missing' })} by ${displayName(invite.used_by_name, 'unknown admin')}`
      : 'Not redeemed'

  return (
    <div data-testid="admin-users" className="space-y-4">
      {inlineError !== null && pending === null && (
        <InlineError message={inlineError} data-testid="admin-users-error" />
      )}

      {/* Admin accounts. PATCH /api/admin/users/:id/role and DELETE /api/admin/users/:id. */}
      <SectionCard
        title={`Admin accounts (${admins.length})`}
        description="Who can sign in to this console. Super admins can also manage other admins and view the console as them."
      >
        {usersQuery.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : usersQuery.isError ? (
          <InlineError message="Could not load admins. Try refreshing." />
        ) : admins.length === 0 ? (
          <div
            data-testid="admin-users-empty"
            className="rounded-md border border-dashed border-border/60 px-4 py-6 text-center text-sm text-muted-foreground"
          >
            No admin accounts. Invite one below.
          </div>
        ) : (
          <ResponsiveRows
            table={
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-full">Admin</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="whitespace-nowrap">Created</TableHead>
                    <TableHead className="whitespace-nowrap">Last sign-in</TableHead>
                    <TableHead>Invited by</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map((admin) => (
                    <TableRow
                      key={admin.id}
                      data-testid="admin-users-row"
                      data-admin-id={admin.id}
                      className={adminBusy(admin) ? 'opacity-60' : undefined}
                    >
                      <TableCell className="min-w-[12rem]">
                        <div className="font-medium text-foreground">{admin.name}</div>
                        <div className="truncate text-xs text-muted-foreground" title={admin.email}>{admin.email}</div>
                      </TableCell>
                      <TableCell>{rolePill(admin)}</TableCell>
                      <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">
                        {formatDateTime(admin.created_at, { empty: 'Unknown' })}
                      </TableCell>
                      <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">
                        {formatDateTime(admin.last_login, { empty: 'Never' })}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {displayName(admin.invited_by_name, '—')}
                      </TableCell>
                      <TableCell className="w-px whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">{adminActions(admin)}</div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            }
            stacked={
              <StackedRows
                rows={admins}
                rowKey={(a) => a.id}
                rowProps={(a) => ({
                  'data-testid': 'admin-users-row',
                  'data-admin-id': a.id,
                  className: adminBusy(a) ? 'opacity-60' : undefined,
                })}
                primary={(a) => (
                  <>
                    <div>{a.name}</div>
                    <div className="truncate text-xs font-normal text-muted-foreground">{a.email}</div>
                  </>
                )}
                secondary={rolePill}
                fields={[
                  { label: 'Created', value: (a) => formatDateTime(a.created_at, { empty: 'Unknown' }), numeric: true },
                  { label: 'Last sign-in', value: (a) => formatDateTime(a.last_login, { empty: 'Never' }), numeric: true },
                  { label: 'Invited by', value: (a) => displayName(a.invited_by_name, '—'), show: (a) => a.invited_by_name !== null },
                ]}
                actions={adminActions}
              />
            }
          />
        )}
      </SectionCard>

      {/* Pending invites. DELETE /api/admin/invites/:id invalidates the link. */}
      <SectionCard
        title={`Pending invites (${pendingInvites.length})`}
        description="Registration links that have been created but not used yet."
      >
        {invitesQuery.isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : invitesQuery.isError ? (
          <InlineError message="Could not load invites. Try refreshing." />
        ) : pendingInvites.length === 0 ? (
          <p data-testid="admin-users-invites-empty" className="text-sm text-muted-foreground">
            No pending invites.
          </p>
        ) : (
          <ResponsiveRows
            table={
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-full">Invitee</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="whitespace-nowrap">Expires</TableHead>
                    <TableHead>Sent by</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingInvites.map((invite) => (
                    <TableRow
                      key={invite.id}
                      data-testid="admin-users-invite-row"
                      className={revokeInvite.isPending && revokeInvite.variables === invite.id ? 'opacity-60' : undefined}
                    >
                      <TableCell className="min-w-[12rem] font-medium text-foreground">{invite.email}</TableCell>
                      <TableCell>{inviteStatusPill(invite)}</TableCell>
                      <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">
                        {formatDateTime(invite.expires_at, { empty: 'No expiry' })}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {displayName(invite.created_by_name, 'Unknown')}
                      </TableCell>
                      <TableCell className="w-px whitespace-nowrap text-right">{inviteActions(invite)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            }
            stacked={
              <StackedRows
                rows={pendingInvites}
                rowKey={(i) => i.id}
                rowProps={() => ({ 'data-testid': 'admin-users-invite-row' })}
                primary={(i) => <span className="block truncate">{i.email}</span>}
                secondary={inviteStatusPill}
                fields={[
                  { label: 'Expires', value: (i) => formatDateTime(i.expires_at, { empty: 'No expiry' }), numeric: true },
                  { label: 'Sent by', value: (i) => displayName(i.created_by_name, 'Unknown') },
                ]}
                actions={inviteActions}
              />
            }
          />
        )}
      </SectionCard>

      {/* Invite history. Non-pending invites are read-only audit rows. */}
      <SectionCard
        title={`Invite history (${inviteHistory.length})`}
        description="Used, expired and invalidated invites."
      >
        {invitesQuery.isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : invitesQuery.isError ? (
          <InlineError message="Could not load invite history. Try refreshing." />
        ) : inviteHistory.length === 0 ? (
          <p className="text-sm text-muted-foreground">No past invites.</p>
        ) : (
          <ResponsiveRows
            table={
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-full">Invitee</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="whitespace-nowrap">Expires</TableHead>
                    <TableHead>Sent by</TableHead>
                    <TableHead>Redeemed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inviteHistory.map((invite) => (
                    <TableRow key={invite.id} data-testid="admin-users-invite-history-row">
                      <TableCell className="min-w-[12rem] font-medium text-foreground">{invite.email}</TableCell>
                      <TableCell>{inviteStatusPill(invite)}</TableCell>
                      <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">
                        {formatDateTime(invite.expires_at, { empty: 'No expiry' })}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {displayName(invite.created_by_name, 'Unknown')}
                      </TableCell>
                      <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">
                        {redeemedLabel(invite)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            }
            stacked={
              <StackedRows
                rows={inviteHistory}
                rowKey={(i) => i.id}
                rowProps={() => ({ 'data-testid': 'admin-users-invite-history-row' })}
                primary={(i) => <span className="block truncate">{i.email}</span>}
                secondary={inviteStatusPill}
                fields={[
                  { label: 'Expires', value: (i) => formatDateTime(i.expires_at, { empty: 'No expiry' }), numeric: true },
                  { label: 'Sent by', value: (i) => displayName(i.created_by_name, 'Unknown') },
                  { label: 'Redeemed', value: redeemedLabel, numeric: true },
                ]}
              />
            }
          />
        )}
      </SectionCard>

      {/* Invite form. POST /api/admin/invite with JSON body {email}. */}
      <SectionCard
        title="Invite an admin"
        description="Creates a one-time registration link for the address below. Send it yourself; nothing is emailed automatically."
      >
        <form
          onSubmit={handleInviteSubmit}
          className="flex flex-wrap items-end gap-2"
          data-testid="admin-users-invite-form"
        >
          <div className="min-w-[16rem] flex-1 space-y-1.5">
            <Label htmlFor="admin-users-invite-email">Email address</Label>
            <Input
              id="admin-users-invite-email"
              data-testid="admin-users-invite-email"
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setInviteUrl(null)
              }}
              placeholder="invitee@example.com"
            />
          </div>
          <Button
            type="submit"
            data-testid="admin-users-invite"
            disabled={email.trim() === '' || createInvite.isPending}
          >
            {createInvite.isPending ? 'Creating…' : 'Create invite link'}
          </Button>
        </form>

        {inviteUrl !== null && (
          <div
            data-testid="admin-users-invite-url"
            role="status"
            className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-cyber-cyan/40 bg-cyber-cyan/10 px-3 py-2"
          >
            <span className="w-full text-xs text-muted-foreground sm:w-auto">Invite link</span>
            <code className="min-w-0 flex-1 truncate font-mono text-xs text-foreground" title={inviteUrl}>
              {inviteUrl}
            </code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => { void handleCopy() }}
              data-testid="admin-users-invite-copy"
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        )}
      </SectionCard>

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null)
        }}
        title={
          pending?.kind === 'revoke-admin'
            ? `Revoke access for ${pending.user.name}?`
            : pending?.kind === 'revoke-invite'
              ? `Invalidate the invite for ${pending.invite.email}?`
              : pending?.kind === 'change-role'
                ? pending.nextRole === 'super_admin'
                  ? `Promote ${pending.user.name} to super admin?`
                  : `Demote ${pending.user.name} to admin?`
                : 'Confirm'
        }
        description={
          pending?.kind === 'revoke-admin'
            ? `${pending.user.email} can no longer sign in. Their stream keys, flows and recordings are kept. This can’t be undone from here.`
            : pending?.kind === 'revoke-invite'
              ? 'The registration link stops working immediately. You can create a new invite at any time.'
              : pending?.kind === 'change-role'
                ? pending.nextRole === 'super_admin'
                  ? 'They gain the right to manage other admins and to view the console as any of them.'
                  : 'They lose the right to manage other admins. Their own keys and flows are unaffected.'
                : ''
        }
        confirmLabel={
          pending?.kind === 'revoke-admin'
            ? 'Revoke access'
            : pending?.kind === 'revoke-invite'
              ? 'Invalidate invite'
              : pending?.nextRole === 'super_admin'
                ? 'Promote'
                : 'Demote'
        }
        busyLabel="Working…"
        confirmVariant={pending?.kind === 'change-role' ? 'default' : 'destructive-solid'}
        busy={confirmBusy}
        error={pending ? inlineError : null}
        returnFocusTo={confirmTriggerRef}
        onConfirm={confirmPending}
        data-testid="admin-users-confirm"
      />
    </div>
  )
}
